/**
 * Real HTTP implementation of the seller API.
 *
 * Selected by src/api/seller.ts when VITE_UI_PREVIEW is NOT 'true'.
 */
import type {
  InvoiceReplaceBody,
  InvoiceReplaceResponse,
  SellerLoginResponse,
  SellerVerifyResponse,
  SellerRegisterResponse,
  SellerDevicesResponse,
  SellerRegistrationDetailResponse,
  SellerServicesResponse,
  ResolveServiceRequestResponse,
  ServiceRequestBody,
  ServiceRequestResponse,
  ServiceRequestsResponse,
  SellerDashboardResponse,
  SellerInstallersResponse,
  SellerInstaller,
  SellerPortalAdminsResponse,
  SellerPortalAdmin,
  SellerProductsResponse,
  LogoutBody,
} from '@m8-app/contracts';
import { apiClient } from './client.js';
import type {
  SellerLoginRequestBodyV2,
  SellerVerifyBodyV2,
  RegisterDeviceBody,
  CreateInstallerBodyV2,
  UpdateInstallerBodyV2,
  AddPortalAdminBodyV2,
} from './seller.types.js';

/* ────────────── seller auth ────────────── */

export async function requestSellerOtp(
  body: SellerLoginRequestBodyV2,
): Promise<SellerLoginResponse> {
  const { data } = await apiClient.post<SellerLoginResponse>(
    '/seller/auth/otp/request',
    body,
  );
  return data;
}

export async function verifySellerOtp(
  body: SellerVerifyBodyV2,
): Promise<SellerVerifyResponse> {
  const { data } = await apiClient.post<SellerVerifyResponse>(
    '/seller/auth/otp/verify',
    body,
  );
  return data;
}

export async function logoutSeller(refreshToken: string): Promise<void> {
  const body: LogoutBody = { refreshToken };
  await apiClient.post('/seller/auth/logout', body);
}

/* ────────────── dashboard ────────────── */

export async function getSellerDashboard(): Promise<SellerDashboardResponse> {
  const { data } = await apiClient.get<SellerDashboardResponse>('/seller/dashboard');
  return data;
}

/* ────────────── device registration ────────────── */

export async function registerDevice(
  body: RegisterDeviceBody,
): Promise<SellerRegisterResponse> {
  const { data } = await apiClient.post<SellerRegisterResponse>(
    '/seller/devices/register',
    body,
  );
  return data;
}

/* ────────────── device queries ────────────── */

export async function getSellerDevices(): Promise<SellerDevicesResponse> {
  const { data } = await apiClient.get<SellerDevicesResponse>('/seller/devices');
  return data;
}

export async function getSellerDevice(id: string): Promise<SellerRegistrationDetailResponse> {
  const { data } = await apiClient.get<SellerRegistrationDetailResponse>(`/seller/devices/${id}`);
  return data;
}

export async function getSellerInvoice(registrationId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/seller/devices/${registrationId}/invoice`, {
    responseType: 'blob',
  });
  return data;
}

export async function replaceSellerInvoice(
  registrationId: string,
  body: InvoiceReplaceBody,
): Promise<InvoiceReplaceResponse> {
  const { data } = await apiClient.put<InvoiceReplaceResponse>(
    `/seller/devices/${registrationId}/invoice`,
    body,
  );
  return data;
}

/* ────────────── service requests ────────────── */

export async function resolveServiceRequest(requestId: string): Promise<ResolveServiceRequestResponse> {
  const { data } = await apiClient.patch<ResolveServiceRequestResponse>(
    `/seller/services/${requestId}/resolve`,
  );
  return data;
}

export async function getAllSellerServices(): Promise<SellerServicesResponse> {
  const { data } = await apiClient.get<SellerServicesResponse>('/seller/services');
  return data;
}

export async function logServiceRequest(
  registrationId: string,
  body: ServiceRequestBody,
): Promise<ServiceRequestResponse> {
  const { data } = await apiClient.post<ServiceRequestResponse>(
    `/seller/devices/${registrationId}/service`,
    body,
  );
  return data;
}

export async function getServiceRequests(
  registrationId: string,
): Promise<ServiceRequestsResponse> {
  const { data } = await apiClient.get<ServiceRequestsResponse>(
    `/seller/devices/${registrationId}/service`,
  );
  return data;
}

/* ────────────── installers ────────────── */

export async function listInstallers(): Promise<SellerInstallersResponse> {
  const { data } = await apiClient.get<SellerInstallersResponse>('/seller/installers');
  return data;
}

export async function createInstaller(body: CreateInstallerBodyV2): Promise<SellerInstaller> {
  const { data } = await apiClient.post<SellerInstaller>('/seller/installers', body);
  return data;
}

export async function updateInstaller(id: string, body: UpdateInstallerBodyV2): Promise<SellerInstaller> {
  const { data } = await apiClient.patch<SellerInstaller>(`/seller/installers/${id}`, body);
  return data;
}

export async function deactivateInstaller(id: string): Promise<void> {
  await apiClient.delete(`/seller/installers/${id}`);
}

/* ────────────── portal admins ────────────── */

export async function listPortalAdmins(): Promise<SellerPortalAdminsResponse> {
  const { data } = await apiClient.get<SellerPortalAdminsResponse>('/seller/portal-admins');
  return data;
}

export async function addPortalAdmin(body: AddPortalAdminBodyV2): Promise<SellerPortalAdmin> {
  const { data } = await apiClient.post<SellerPortalAdmin>('/seller/portal-admins', body);
  return data;
}

export async function removePortalAdmin(id: string): Promise<void> {
  await apiClient.delete(`/seller/portal-admins/${id}`);
}

/* ────────────── product selection ────────────── */

export async function getSellerProducts(): Promise<SellerProductsResponse> {
  const { data } = await apiClient.get<SellerProductsResponse>('/seller/products');
  return data;
}

export async function setSellerProducts(selectedProducts: string[]): Promise<SellerProductsResponse> {
  const { data } = await apiClient.put<SellerProductsResponse>('/seller/products', { selectedProducts });
  return data;
}
